/* global ve */
// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import './polyfill'
import { createApp, configureCompat } from 'vue'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
import App from './App'
import router from './router/index.js'
import miniToastr from 'mini-toastr' // https://github.com/se-panfilov/mini-toastr
import Loading from 'vue-loading-overlay'
import moment from 'moment'
import { vue3Debounce } from 'vue-debounce'
import VueGoogleMaps from '@fawmi/vue-google-maps'
import VueApexCharts from 'vue3-apexcharts'
import VueCodeMirror from 'vue-codemirror'
import VueResize from 'vue-resize'
import MaskedInput from 'vue-text-mask'
import VCalendar from 'v-calendar'
import { StreamBarcodeReader } from 'vue-barcode-reader'
import VueBarcode from 'vue-barcode'
import Toasted from 'vue-toasted'
import veMixins from './js/veMixins'
import { stickyHeaders } from './js/customDirectives'
import APIError from './APIError.vue'
import Pagination from './views/base/Pagination.vue'
import TableCell from './views/base/TableCell.vue'
import vueJsonEditor from 'vue-json-editor'
import Multiselect from 'vue-multiselect'
import VueSpeedMeter from 'vue-speed-meter'
import { createI18n } from 'vue-i18n'
import messages from './translation'
import CountryFlag from 'vue-country-flag-next'
import { QuillEditor } from '@vueup/vue-quill'
import { createPinia } from 'pinia'
import { useGoogleMapsStore } from './stores/googleMaps'

// Font Awesome 7
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { fab } from '@fortawesome/free-brands-svg-icons'
// Import stylesheet
import 'vue-loading-overlay/dist/css/index.css'
import 'bootstrap/dist/css/bootstrap.css'
import 'v-calendar/dist/style.css'
// import cssVars from 'css-vars-ponyfill'
// import 'bootstrap-vue-3/dist/bootstrap-vue-3.css'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
import JSONbig from 'json-bigint'
// Import javascript
import * as veb from './js/veb.js'
import * as veapi from './js/veapi.js'
import { initPageVue, setAppInstance } from './js/veinit.js' // Import initPageVue from the new module

// Amplify
import { Amplify } from 'aws-amplify'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import veAmplify from './js/veamplify.js'
import veSatUser from './js/vesatuser.js'

import hljs from 'highlight.js/lib/core'
import xml from 'highlight.js/lib/languages/xml'
import hljsVuePlugin from '@highlightjs/vue-plugin'
import 'highlight.js/styles/atom-one-light.css'

library.add(fas, far, fab)

hljs.registerLanguage('xml', xml)

const fn = 'main'
ve.veb = veb
ve.veapi = veapi
ve.su = veSatUser
ve.amplify = veAmplify
ve.Amplify = Amplify

// Configure JSONbig to store large numbers as strings
const JSONbigString = JSONbig({ storeAsString: true })

const toastTypes = {
  success: 'success',
  error: 'error',
  info: 'info',
  warn: 'warn'
}

miniToastr.init({ types: toastTypes })

configureCompat({ WATCH_ARRAY: false })

const veEnvLoc = ve.veb.getVeEnvLoc(true)
ve.veb.doLog(fn, { veEnvLoc }, true)

ve.amplify.initAmplifyConfig(veEnvLoc)

moment.updateLocale('en', {
  longDateFormat: {
    LT: 'h:mm:ss A',
    L: 'MM/DD/YYYY',
    l: 'M/D/YYYY',
    LL: 'MMMM Do YYYY',
    ll: 'MMM D YYYY',
    LLL: 'MMMM Do YYYY LT',
    lll: 'MMM D YYYY LT',
    LLLL: 'dddd, MMMM Do YYYY LT',
    llll: 'ddd, MMM D YYYY LT'
  }
})

const i18n = createI18n({
  locale: 'en',
  allowComposition: true,
  globalInjection: true,
  fallbackLocale: 'en',
  messages
})

const veI18n = {
  install (app, options) {
    app.config.globalProperties.$veT = function (param) {
      if (param === '') return ''
      return this.$t(param)
    }
  }
}

async function initializeApp () {
  let googleMapsApiKey = null
  try {
    console.log('initializeApp:start')
    // Attempt to fetch session and initialize post-login processes
    // This block is now active to ensure authentication is checked and session is set
    // if (await ve.amplify.isNotAuthenticated(true)) ve.amplify.startCognitoLogin(veEnvLoc, true)
    if (await ve.amplify.isAuthenticated(true)) { // Check if already authenticated
      await getCurrentUser() // This might refresh tokens if needed
      const session = await fetchAuthSession() // Fetch the current session
      console.log('initializeApp: fetched session', session)
      ve.amplify.setSession(session) // Set the session in ve.amplify
      await ve.initPostLogin(true) // Perform post-login initialization
      googleMapsApiKey = await veapi.getGoogleMapsApiKey() // Fetch API key after authentication
      console.log('initializeApp: Google Maps API Key length', `${googleMapsApiKey.length}`)
    } else {
      console.log('initializeApp: User not authenticated, skipping Google Maps API key fetch.')
      // If not authenticated, the common.js logic should handle the redirect to Cognito Hosted UI.
      // We don't need to explicitly call startCognitoLogin here, as it's handled by common.js
      // when a protected route is accessed.
    }
  } catch (error) {
    console.error('User not authenticated, or failed to get Google Maps API key', error)
  }

  const pinia = createPinia()

  const app = createApp(App)
  setAppInstance(app)
  app.use(hljsVuePlugin)
  app.component(App)
  app.component('FontAwesomeIcon', FontAwesomeIcon)
  app.component('APIError', APIError)
  app.component('Multiselect', Multiselect)
  app.component('Pagination', Pagination)
  app.component('TableCell', TableCell)
  app.component('VueJsonEditor', vueJsonEditor)
  app.component('VueSpeedMeter', VueSpeedMeter)
  app.component('StreamBarcodeReader', StreamBarcodeReader)
  app.component('CountryFlag', CountryFlag)
  app.component('QuillEditor', QuillEditor)

  // Register Font Awesome component globally
  app.component('FontAwesomeIcon', FontAwesomeIcon)

  app.mixin(veMixins)

  app.directive('debounce', vue3Debounce({ lock: true }))
  app.directive('stickyHeaders', stickyHeaders)

  app.use(router)
  app.use(BootstrapVue)
  app.use(BootstrapVueIcons)
  app.use(Loading)
  app.use(pinia)

  if (googleMapsApiKey) {
    app.use(VueGoogleMaps, { load: { key: googleMapsApiKey } })
    const googleMapsStore = useGoogleMapsStore()
    googleMapsStore.setLoaded(true)
  }

  app.use(VueApexCharts)
  app.use(VueCodeMirror)
  app.use(VueResize)
  app.use(MaskedInput)
  app.use(VCalendar, {
    firstDayOfWeek: 2 // Monday
  })
  app.use(VueBarcode)
  app.use(Toasted)
  app.use(i18n)
  app.use(veI18n)
  // Make it available globally as $JSONbig
  app.config.globalProperties.$JSONbig = JSONbigString
  app.mount('#app')
  console.log('router: ', router)
}

initializeApp().catch(error => {
  console.error('Failed to initialize the application:', error)
})

// Call initPageVue after the main application initialization
// This ensures the authentication flow is initiated early in the application lifecycle.
// Call initGlobalVars before initPageVue
ve.initGlobalVars() // Initialize global ve variables
ve.veb.doLog(fn, { info: 'calling initPageVue from main.js' }, true)
initPageVue(ve.cfg, true).catch(error => {
  console.error('Failed to initialize page Vue:', error)
})

export { Amplify, i18n }
